import { ETrackingEventType } from '@constants';
import { IEvent } from '@sdk/contracts';
import { dateProvider } from '@utils';
import { Button } from 'antd';
import * as dateFns from 'date-fns';
import { useMemo } from 'react';

import { OngoingEventItem } from '../../OngoingEventItem';

import { Wrapper, InfoWrapper, TitleWrapper, TimeWrapper, StyledButton } from './EventItem.styles';

import { Icon } from '@/components/Icon';
import { useTrackingEvents } from '@/hooks';
import { useMutateEvents, useProfile } from '@/store';

const ICON_SIZE = 18;

type Props = {
  event: IEvent;
  onClick: () => void;
};
export const EventItem: React.FC<Props> = ({ event, onClick }) => {
  const { profile } = useProfile();
  const { startDate, endDate, title } = event;
  const utcStartDate = dateProvider.zonedTimeToUtc(startDate);
  const utcEndDate = dateProvider.zonedTimeToUtc(endDate);
  const { addAttendee, removeAttendee, isWorking } = useMutateEvents();
  const { trackEvent } = useTrackingEvents();

  const isUserAttending = useMemo(() => {
    if (!profile) return false;
    return event.attendees.some(attendee => attendee._id === profile._id);
  }, [profile, event]);

  const isEventStarted = useMemo(() => {
    if (!event || !event.startDate || !event.endDate) return false;
    const now = dateProvider.now();
    const startDateEvent = event.startDate;
    const endDateEvent = event.endDate;

    const adjustedStartTime = dateProvider.subtract(
      dateProvider.zonedTimeToUtc(startDateEvent),
      10,
      'minutes'
    );
    const endTime = dateProvider.zonedTimeToUtc(endDateEvent);
    if (!dateFns.isValid(adjustedStartTime) || !dateFns.isValid(endTime)) {
      return false;
    }
    if (adjustedStartTime >= endTime) {
      return false;
    }
    return dateFns.isWithinInterval(now, {
      start: adjustedStartTime,
      end: endTime
    });
  }, [event]);

  const handleAttendEvent = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!profile) return;
    if (isUserAttending) {
      removeAttendee({ eventId: event._id, attendeeId: profile._id });
    } else {
      trackEvent({
        eventType: ETrackingEventType.LIVE_CLASS_ADDED,
        eventData: {
          liveClassEventId: event._id
        }
      });
      addAttendee({ eventId: event._id, attendeeId: profile._id });
    }
  };

  const renderActionButton = (isOngoing: boolean) => {
    if (isOngoing) {
      return <OngoingEventItem event={event} />;
    }
    return renderAttendButton();
  };

  const renderAttendButton = () => {
    if (isUserAttending) {
      return (
        <StyledButton
          loading={isWorking}
          onClick={handleAttendEvent}
          shape="default"
          type="tertiary"
          icon={<Icon i="Checkmark" color="green7" size={ICON_SIZE} />}
          size="small"
        />
      );
    }
    return (
      <Button
        loading={isWorking}
        onClick={handleAttendEvent}
        shape="default"
        icon={<Icon i="CalendarAdd" size={ICON_SIZE} />}
        size="small"
      />
    );
  };

  return (
    <Wrapper onClick={onClick}>
      {isEventStarted ? (
        renderActionButton(isEventStarted)
      ) : (
        <InfoWrapper>
          <TitleWrapper>{title}</TitleWrapper>
          <TimeWrapper>
            {`${dateProvider.format(utcStartDate, 'HH:mm')} - ${dateProvider.format(
              utcEndDate,
              'HH:mm'
            )}`}
          </TimeWrapper>
        </InfoWrapper>
      )}
      {!isEventStarted && renderAttendButton()}
    </Wrapper>
  );
};
